import { ConfigProvider, theme as antdTheme } from 'antd';
import jaJP from 'antd/locale/ja_JP';
import type { ThemeConfig } from 'antd/es/config-provider/context';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { trpc } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { FONT_FAMILY_BASE } from '../constants/fonts';

type ThemeMode = 'light' | 'dark' | 'system' | 'mild';
type FontSizeOption = 'small' | 'medium' | 'large';
type TimeFormatOption = '24h' | '12h';
type WeekStartOption = 'monday' | 'sunday';
type DigestFrequency = 'realtime' | 'hourly' | 'daily' | 'weekly';

type NotificationChannelKey = string;

export type NotificationChannelPreferences = {
  inApp?: boolean;
  email?: boolean;
  sms?: boolean;
  slack?: boolean;
};

export interface UserPreferences {
  appearance: {
    theme: ThemeMode;
    fontSize: FontSizeOption;
    compactSidebar: boolean;
    showTooltips: boolean;
    reduceMotion: boolean;
  };
  locale: {
    language: string;
    timezone: string;
    dateFormat: string;
    timeFormat: TimeFormatOption;
    weekStartsOn: WeekStartOption;
  };
  dashboard: {
    widgets: string[];
    startPage: string;
  };
  notifications: {
    categories: Record<NotificationChannelKey, NotificationChannelPreferences>;
    digest: {
      frequency: DigestFrequency;
      time: string;
      dayOfWeek: WeekStartOption;
    };
    quietHours: {
      enabled: boolean;
      start: string;
      end: string;
    };
  };
}

interface UserPreferencesContextValue {
  preferences: UserPreferences;
  effectiveTheme: 'light' | 'dark' | 'mild';
  isLoading: boolean;
  updateLocalPreferences: (partial: Partial<UserPreferences>) => void;
  replacePreferences: (preferences: Partial<UserPreferences> | null | undefined) => void;
}

const defaultNotificationChannels: Record<NotificationChannelKey, NotificationChannelPreferences> = {
  systemAlerts: { inApp: true, email: true, sms: false, slack: false },
  workAssignments: { inApp: true, email: true, slack: false },
  approvals: { inApp: true, email: true },
  reports: { inApp: true, email: true },
  maintenance: { inApp: true, slack: false },
  systemMaintenance: { inApp: true, email: true },
};

const defaultPreferences: UserPreferences = {
  appearance: {
    theme: 'light',
    fontSize: 'medium',
    compactSidebar: false,
    showTooltips: true,
    reduceMotion: false,
  },
  locale: {
    language: 'ja-JP',
    timezone: 'Asia/Tokyo',
    dateFormat: 'YYYY/MM/DD',
    timeFormat: '24h',
    weekStartsOn: 'monday',
  },
  dashboard: {
    widgets: ['summary', 'alerts', 'schedule', 'deterioration'],
    startPage: '/app/dashboard',
  },
  notifications: {
    categories: defaultNotificationChannels,
    digest: {
      frequency: 'daily',
      time: '09:00',
      dayOfWeek: 'monday',
    },
    quietHours: {
      enabled: false,
      start: '22:00',
      end: '07:00',
    },
  },
};


const clonePreferences = (prefs: UserPreferences): UserPreferences => ({
  appearance: { ...prefs.appearance },
  locale: { ...prefs.locale },
  dashboard: { ...prefs.dashboard, widgets: [...prefs.dashboard.widgets] },
  notifications: {
    categories: Object.entries(prefs.notifications.categories).reduce((acc, [key, value]) => {
      acc[key as NotificationChannelKey] = { ...(value ?? {}) };
      return acc;
    }, {} as Record<NotificationChannelKey, NotificationChannelPreferences>),
    digest: { ...prefs.notifications.digest },
    quietHours: { ...prefs.notifications.quietHours },
  },
});

const UserPreferencesContext = createContext<UserPreferencesContextValue | undefined>(undefined);

const fallbackContext: UserPreferencesContextValue = {
  preferences: clonePreferences(defaultPreferences),
  effectiveTheme: 'light',
  isLoading: false,
  updateLocalPreferences: () => {
    if (import.meta.env.DEV) {
      console.warn('UserPreferencesProvider is missing. Falling back to default preferences.');
    }
  },
  replacePreferences: () => {
    if (import.meta.env.DEV) {
      console.warn('UserPreferencesProvider is missing. Falling back to default preferences.');
    }
  },
};


const mergePreferences = (
  base: UserPreferences,
  overrides?: Partial<UserPreferences> | null,
): UserPreferences => {
  const merged = clonePreferences(base);
  if (!overrides) {
    return merged;
  }

  if (overrides.appearance) {
    merged.appearance = { ...merged.appearance, ...overrides.appearance };
  }

  if (overrides.locale) {
    merged.locale = { ...merged.locale, ...overrides.locale };
  }

  if (overrides.dashboard) {
    merged.dashboard = {
      ...merged.dashboard,
      ...overrides.dashboard,
      widgets: overrides.dashboard.widgets ?? merged.dashboard.widgets,
    };
  }

  if (overrides.notifications) {
    const currentCategories = merged.notifications.categories ?? {};
    const overrideCategories = overrides.notifications.categories ?? {};
    const categories: Record<NotificationChannelKey, NotificationChannelPreferences> = {
      ...currentCategories,
    };
    Object.entries(overrideCategories).forEach(([key, value]) => {
      categories[key as NotificationChannelKey] = {
        ...(currentCategories[key as NotificationChannelKey] ?? {}),
        ...(value ?? {}),
      };
    });

    merged.notifications = {
      categories,
      digest: {
        ...merged.notifications.digest,
        ...overrides.notifications.digest,
      },
      quietHours: {
        ...merged.notifications.quietHours,
        ...overrides.notifications.quietHours,
      },
    };
  }

  return merged;
};

const useResolvedSystemTheme = () => {
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window === 'undefined') {
      return 'light';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = (event: MediaQueryListEvent) => {
      setSystemTheme(event.matches ? 'dark' : 'light');
    };

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', listener);
      return () => media.removeEventListener('change', listener);
    }
    media.addListener(listener);
    return () => media.removeListener(listener);
  }, []);

  return systemTheme;
};

/**
 * pathname の変更を監視するカスタムフック
 * React Router が RouterProvider の外側で使用されるため、
 * history API を直接監視する
 */
const usePathname = () => {
  const [pathname, setPathname] = useState(() => {
    if (typeof window === 'undefined') return '/';
    return window.location.pathname;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updatePathname = () => setPathname(window.location.pathname);

    // ブラウザの戻る/進むボタン
    window.addEventListener('popstate', updatePathname);

    // React Router の pushState/replaceState を監視
    const originalPushState = history.pushState.bind(history);
    const originalReplaceState = history.replaceState.bind(history);

    history.pushState = (...args) => {
      originalPushState(...args);
      updatePathname();
    };

    history.replaceState = (...args) => {
      originalReplaceState(...args);
      updatePathname();
    };

    return () => {
      window.removeEventListener('popstate', updatePathname);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, []);

  return pathname;
};

/**
 * テーマを適用すべきルートかどうかを判定
 * /app と /admin 配下でのみユーザー設定のテーマを適用
 */
const isThemeApplicableRoute = (pathname: string): boolean => {
  return pathname.startsWith('/app') || pathname.startsWith('/admin');
};

export const UserPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  const pathname = usePathname();
  const shouldApplyTheme = isThemeApplicableRoute(pathname);
  const { data: profile, isFetching } = trpc.user.getProfile.useQuery(undefined, {
    enabled: isAuthenticated && shouldApplyTheme,
    staleTime: 1000 * 60 * 5,
    suspense: false,
  });

  const [preferences, setPreferences] = useState<UserPreferences>(() => clonePreferences(defaultPreferences));
  const systemTheme = useResolvedSystemTheme();

  const replacePreferences = useCallback((next: Partial<UserPreferences> | null | undefined) => {
    setPreferences(mergePreferences(defaultPreferences, next));
  }, []);

  const updateLocalPreferences = useCallback((partial: Partial<UserPreferences>) => {
    setPreferences((prev) => mergePreferences(prev, partial));
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      replacePreferences(null);
      return;
    }
    replacePreferences(profile?.preferences as Partial<UserPreferences> | undefined);
  }, [isAuthenticated, profile?.preferences, replacePreferences]);

  const effectiveTheme: 'light' | 'dark' | 'mild' = useMemo(() => {
    if (preferences.appearance.theme === 'system') {
      return systemTheme;
    }
    if (preferences.appearance.theme === 'mild') {
      return 'mild';
    }
    return preferences.appearance.theme;
  }, [preferences.appearance.theme, systemTheme]);

  // テーマ適用: /app, /admin 配下でのみユーザー設定を適用、それ以外はデフォルト（light）
  useEffect(() => {
    const themeToApply = shouldApplyTheme ? effectiveTheme : 'light';
    document.documentElement.setAttribute('data-app-theme', themeToApply);
  }, [effectiveTheme, shouldApplyTheme]);

  useEffect(() => {
    const themeToApply = shouldApplyTheme ? effectiveTheme : 'light';
    document.body.classList.toggle('app-theme-mild', themeToApply === 'mild');
  }, [effectiveTheme, shouldApplyTheme]);

  // フォントサイズ: /app, /admin 配下でのみユーザー設定を適用、それ以外はデフォルト（medium）
  useEffect(() => {
    const body = document.body;
    body.classList.remove('user-font-small', 'user-font-medium', 'user-font-large');
    const fontSizeToApply = shouldApplyTheme ? preferences.appearance.fontSize : 'medium';
    body.classList.add(`user-font-${fontSizeToApply}`);
  }, [preferences.appearance.fontSize, shouldApplyTheme]);

  useEffect(() => {
    const hideTooltips = shouldApplyTheme ? !preferences.appearance.showTooltips : false;
    document.body.classList.toggle('user-hide-tooltips', hideTooltips);
  }, [preferences.appearance.showTooltips, shouldApplyTheme]);

  useEffect(() => {
    const reduceMotion = shouldApplyTheme ? preferences.appearance.reduceMotion : false;
    document.body.classList.toggle('user-reduce-motion', reduceMotion);
  }, [preferences.appearance.reduceMotion, shouldApplyTheme]);

  // ConfigProvider 用の設定: /app, /admin 配下でのみユーザー設定を適用
  const componentSize = useMemo((): 'small' | 'middle' | 'large' => {
    if (!shouldApplyTheme) return 'middle'; // デフォルト
    switch (preferences.appearance.fontSize) {
      case 'small':
        return 'small';
      case 'large':
        return 'large';
      default:
        return 'middle';
    }
  }, [preferences.appearance.fontSize, shouldApplyTheme]);

  const themeTokens = useMemo<ThemeConfig['token']>(() => {
    const fontSize = shouldApplyTheme ? preferences.appearance.fontSize : 'medium';
    const baseFontSize = fontSize === 'large' ? 16 : fontSize === 'small' ? 12 : 14;
    const tokens: ThemeConfig['token'] = {
      fontSize: baseFontSize,
      fontFamily: FONT_FAMILY_BASE,
    };

    if (shouldApplyTheme && preferences.appearance.compactSidebar) {
      tokens.paddingLG = 16;
      tokens.paddingMD = 12;
      tokens.paddingSM = 8;
    }

    const themeToApply = shouldApplyTheme ? effectiveTheme : 'light';
    if (themeToApply === 'dark') {
      Object.assign(tokens, {
        colorLink: '#5eccffff',
      });
    }

    else if (themeToApply === 'mild') {
      Object.assign(tokens, {
        colorBgBase: '#f8f4e9',
        colorBgContainer: '#fffaf0',
        colorBgLayout: '#f6f0e1',
        colorBgElevated: '#fff7de',
        colorPrimary: '#d4a373',
        colorPrimaryHover: '#c18b4f',
        colorLink: '#d4a373',
        colorBorder: '#eadfc8',
        colorBorderSecondary: '#eadfc8',
        colorText: '#3f3727',
        colorTextSecondary: '#625846',
      });
    }

    return tokens;
  }, [preferences.appearance.fontSize, preferences.appearance.compactSidebar, effectiveTheme, shouldApplyTheme]);

  const algorithm = useMemo(() => {
    const themeToApply = shouldApplyTheme ? effectiveTheme : 'light';
    return themeToApply === 'dark'
      ? [antdTheme.darkAlgorithm]
      : [antdTheme.defaultAlgorithm];
  }, [effectiveTheme, shouldApplyTheme]);

  const contextValue = useMemo<UserPreferencesContextValue>(() => ({
    preferences,
    effectiveTheme,
    isLoading: isAuthenticated && isFetching,
    updateLocalPreferences,
    replacePreferences,
  }), [preferences, effectiveTheme, isAuthenticated, isFetching, updateLocalPreferences, replacePreferences]);

  return (
    <UserPreferencesContext.Provider value={contextValue}>
      <ConfigProvider
        locale={jaJP}
        componentSize={componentSize}
        wave={{ disabled: shouldApplyTheme ? preferences.appearance.reduceMotion : false }}
        theme={{
          algorithm,
          token: themeTokens,
        }}
      >
        {children}
      </ConfigProvider>
    </UserPreferencesContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    return fallbackContext;
  }
  return context;
};
